import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Target, BookOpen, Zap, Award, Users, TrendingUp, Check } from 'lucide-react';
import { SEO } from '@/components/SEO';

const IMAGES = {
  team_photo: "https://customer-assets.emergentagent.com/job_cxo-placement/artifacts/my1x077o_ChatGPT%20Image%20Mar%208%2C%202026%2C%2010_13_56%20AM.png",
};

const values = [
  { icon: Target, title: 'Right Vision', desc: 'We see the complete picture of your business landscape and chart the optimal path forward.' },
  { icon: BookOpen, title: 'Right Knowledge', desc: 'MBA and CA expertise combined with deep industry experience ensures informed decision-making.' },
  { icon: Zap, title: 'Right Action', desc: 'Precise execution with attention to compliance, timelines, and sustainable growth.' },
];

const differentiators = [
  'CA-led technical expertise with MBA-driven strategic thinking',
  'Built specifically for micro businesses and non-profits',
  'Integrated finance and hiring support under one roof',
  'Support for women entrepreneurs through the SriRatna community',
];

export default function AboutPage() {
  return (
    <div className="pt-20" data-testid="about-page">
      <SEO 
        title="About Us - MBA & CA Led Consulting for Micro Businesses"
        path="/about"
        description="Ratnatrraya Consulting LLP was founded by MBA and CA professionals to give micro businesses and NGOs access to high-quality financial and leadership support. Based in Hyderabad & Mumbai."
        keywords="Ratnatrraya Consulting, about us, CA firm Hyderabad, micro business advisory, Priyaa V Jain, Priyanka Golchha"
      />
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fadeInUp">
              <span className="text-sm font-medium tracking-widest text-[#E84C3D] uppercase mb-4 block">About Us</span>
              <h1 className="font-secondary text-4xl md:text-5xl font-bold text-slate-900 leading-tight mb-6">
                The Three Jewels of Business Excellence
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Ratnatrraya, meaning "Three Jewels" in Sanskrit, reflects our founding philosophy: 
                <strong className="text-slate-900"> Right Vision</strong>, 
                <strong className="text-slate-900"> Right Knowledge</strong>, and 
                <strong className="text-slate-900"> Right Action</strong>.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                We exist to give micro businesses and non-profits the same quality of financial and leadership support that large organisations enjoy — in a way that is simple, practical, and affordable.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                For many of our clients, we act as a <strong className="text-slate-900">Micro CFO layer</strong> — bringing clarity, discipline, and structure to their financial decisions.
              </p>
              <Link to="/team">
                <Button className="bg-[#E84C3D] hover:bg-[#C0392B] text-white">
                  Meet Our Team <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="animate-fadeInUp animation-delay-200">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="bg-white p-5 rounded-xl shadow-lg">
                    <Award className="w-8 h-8 text-[#E84C3D] mb-3" />
                    <h4 className="font-semibold text-slate-900 mb-1 text-sm">MBA + CA Expertise</h4>
                    <p className="text-xs text-slate-600">Strategic thinking meets technical excellence</p>
                  </div>
                  <div className="bg-slate-900 p-5 rounded-xl text-white">
                    <Users className="w-8 h-8 text-[#E84C3D] mb-3" />
                    <h4 className="font-semibold mb-1 text-sm">Client-First</h4>
                    <p className="text-xs text-slate-400">Your success is our success</p>
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="bg-[#059669] p-5 rounded-xl text-white">
                    <TrendingUp className="w-8 h-8 mb-3" />
                    <h4 className="font-semibold mb-1 text-sm">Growth Focus</h4>
                    <p className="text-xs text-green-100">Building sustainable businesses</p>
                  </div>
                  <div className="bg-white p-5 rounded-xl shadow-lg">
                    <Target className="w-8 h-8 text-slate-900 mb-3" />
                    <h4 className="font-semibold text-slate-900 mb-1 text-sm">End-to-End</h4>
                    <p className="text-xs text-slate-600">Complete solutions under one roof</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are Right For */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="bg-[#F9F9F7] p-8 md:p-10 rounded-2xl">
            <h2 className="font-secondary text-2xl md:text-3xl font-semibold text-slate-900 mb-4">
              Who We Are Right For
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              We work best with <strong className="text-slate-900">micro businesses (0–10 Cr revenue)</strong> across manufacturing, services, and trading, 
              as well as <strong className="text-slate-900">NGOs and non-profits</strong>.
            </p>
            <p className="text-slate-600 leading-relaxed mb-6">
              Our typical clients do not have an internal finance team, struggle with poor or delayed reporting, or need help getting their compliance in order.
            </p>
            <p className="text-slate-600 leading-relaxed">
              For larger organisations, we specialise in <strong className="text-slate-900">executive hiring</strong> — finding CFOs, Tax Heads, 
              and Senior Legal Counsel for corporates, PE/VC-backed companies, and investment funds.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 md:py-28 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-medium tracking-widest text-[#E84C3D] uppercase mb-4 block">Our Story</span>
              <h2 className="font-secondary text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                Built on Experience, Driven by Purpose
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Ratnatrraya Consulting LLP was founded by <strong className="text-slate-900">Priyaa V Jain</strong>, an MBA with extensive banking and insurance experience, 
                  and <strong className="text-slate-900">Priyanka Golchha</strong>, a qualified Chartered Accountant.
                </p>
                <p>
                  <strong className="text-slate-900">Priyaa</strong> leads client relationships and the executive hiring vertical, 
                  leveraging deep networks in finance and leadership circles.
                </p>
                <p>
                  <strong className="text-slate-900">Priyanka</strong> anchors the finance and compliance engine and acts as a Micro CFO for small businesses, ensuring clarity, discipline, and reliable financial systems.
                </p>
                <p>
                  Together, they saw that micro businesses and non-profits needed accessible, 
                  high-quality financial services traditionally reserved for larger enterprises — and built Ratnatrraya to close that gap.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-[#E84C3D]/5 rounded-3xl transform -rotate-2"></div>
              <img 
                src={IMAGES.team_photo} 
                alt="Priyaa V Jain and Priyanka Golchha - Ratnatrraya Consulting LLP Partners" 
                className="relative rounded-xl w-full shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="font-secondary text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              What Makes Us Different
            </h2>
          </div>
          <div className="max-w-2xl mx-auto">
            <div className="space-y-4">
              {differentiators.map((item, index) => (
                <div key={index} className="flex items-start gap-4 bg-[#F9F9F7] p-5 rounded-xl">
                  <div className="w-8 h-8 rounded-full bg-[#E84C3D] flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-slate-700 font-medium">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#F9F9F7]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-secondary text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
            Ready to Partner with Us?
          </h2>
          <p className="text-lg text-slate-600 mb-4">
            Whether you are a growing business needing financial support or an organisation seeking executive talent, we are here to help.
          </p>
          <p className="text-base text-slate-500 mb-8">
            Schedule a free consultation and explore how Right Vision, Right Knowledge, and Right Action can support your next stage of growth.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-[#E84C3D] hover:bg-[#C0392B] text-white px-6 py-5">
                Schedule a Free Consultation <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/services">
              <Button variant="outline" className="px-6 py-5">
                Explore Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
